import type { ProductType } from '@/api/models';
import {
    PRODUCT_FILTER_GROUPS,
    type ProductFilterGroup,
    type ProductListDrawerFilters,
} from '../productListFilters';
import {
    PRODUCT_FILTER_DESCRIPTORS,
    PRODUCT_FILTER_DESCRIPTOR_LIST,
} from '../productFilterDescriptors';
import { PRODUCT_TYPE_OPTIONS } from '../productsMeta';
import type { NamedOption } from '../namedOption';
import type { AppliedFilterTag } from './appliedFilterTag';
import type { CategoryTree } from './categoryPickerMeta';
import type { FilterWords } from './filterLabels';

export { nameOfOption } from '../namedOption';
export type { NamedOption } from '../namedOption';
export type { AppliedFilterTag } from './appliedFilterTag';
export { FILTERS_COPY } from './filterLabels';
export type { FilterWords } from './filterLabels';

export type FilterPickerSubject = 'brand' | 'category';
export type FilterOptionsSubject = FilterPickerSubject | 'branch';

export const toProductTypeFilter = (
    keys: Iterable<unknown>,
): ProductType | null => {
    const [first] = keys;
    return (
        PRODUCT_TYPE_OPTIONS.find((option) => option === first) ??
        PRODUCT_FILTER_DESCRIPTORS.type.cleared.type
    );
};

/** Narrows the keys the tags row removed to the filter groups they name. */
export const toFilterGroups = (
    keys: Iterable<unknown>,
): ProductFilterGroup[] => {
    const removed = new Set(keys);
    return PRODUCT_FILTER_GROUPS.filter((group) => removed.has(group));
};

/** One tag per active descriptor, in drawer order. */
export const buildAppliedFilterTags = (
    filters: ProductListDrawerFilters,
    categoryTree: CategoryTree,
    brandOptions: readonly NamedOption[],
    branchOptions: readonly NamedOption[],
): AppliedFilterTag[] =>
    PRODUCT_FILTER_DESCRIPTOR_LIST.flatMap((descriptor) => {
        if (!descriptor.isActive(filters)) {
            return [];
        }
        const tag = descriptor.buildTag(filters, {
            categoryTree,
            brandOptions,
            branchOptions,
        });
        return tag === null ? [] : [tag];
    });

export type FilterTagWords = { nameKey: string; values: FilterWords[] };

/** The tags row and the workbook share each descriptor's wording. */
export const describeAppliedFilterTag = (
    tag: AppliedFilterTag,
): FilterTagWords => {
    const descriptor = PRODUCT_FILTER_DESCRIPTORS[tag.group];
    return {
        nameKey: descriptor.nameKey,
        values: descriptor.describeTag(tag),
    };
};

/** How many values a tag spells out before the rest become "+N". */
const TAG_VALUES_SHOWN = 2;

/**
 * Shortens a tag's values to "A, B +3". The tag shows the full list on hover,
 * so nothing is lost, only folded.
 */
export const summarizeTagValues = (
    values: readonly string[],
): { shown: string[]; hiddenCount: number } => ({
    shown: values.slice(0, TAG_VALUES_SHOWN),
    hiddenCount: Math.max(values.length - TAG_VALUES_SHOWN, 0),
});

/**
 * The brand picker's key for "No brand" (BOS-201). Brand ids are server-made,
 * so this never names a real brand.
 */
export const NO_BRAND_KEY = '__no-brand__';

/** The drawer's brand fields, which the brand picker edits as one value. */
export type BrandFilterValue = Pick<
    ProductListDrawerFilters,
    'brandIds' | 'noBrand'
>;

/** The picker's selection for the brand fields: "No brand" first, as it's listed. */
export const toBrandPickerKeys = ({
    brandIds,
    noBrand,
}: BrandFilterValue): string[] =>
    noBrand ? [NO_BRAND_KEY, ...brandIds] : [...brandIds];

/** Narrows the picker's selected keys back to the brand fields. */
export const fromBrandPickerKeys = (
    keys: Iterable<unknown>,
): BrandFilterValue => {
    const picked = [...new Set([...keys].map(String))];
    return {
        brandIds: picked.filter((key) => key !== NO_BRAND_KEY),
        noBrand: picked.includes(NO_BRAND_KEY),
    };
};

/**
 * The brand picker's search (BOS-201): a brand matches when its name
 * contains what's typed; "No brand" shows only while nothing is typed, so a
 * search lists brands alone. `contains` is the locale-aware matcher.
 */
export const matchesBrandSearch =
    (contains: (text: string, search: string) => boolean) =>
    (text: string, search: string, node: { key: unknown }): boolean =>
        node.key === NO_BRAND_KEY
            ? search.length === 0
            : contains(text, search);
